import OutboundDealsApi from 'api/admins/outbound-deals-api'
import { TitlePage } from 'components/atoms'
import { PER_PAGE_OPTIONS, SCOPE, SORT_OPTIONS } from 'domains/outbound/deals/constants'
import { OutboundDealsPagingFilterField } from 'domains/outbound/deals/fields'
import { OutboundDealsFilterForm } from 'domains/outbound/deals/forms'
import { OutboundDealsUrlParamsTable } from 'domains/outbound/deals/url-params-table'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Badge, Button, Card, Col, Row } from 'reactstrap'
import ParamsUtil from 'utils/params-util'

const DEFAULT_FILTERS = {
  page         : 1,
  scope        : SCOPE.NEED_PACKING,
  name         : '',
  email        : '',
  phone        : '',
  order_number : '',
  sku          : '',
}

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const OutboundShipmentListPage = ({ pageUtils }) => {
  const history  = useHistory()
  const location = useLocation()

  const [deals, setDeals]             = useState([])
  const [pagination, setPagination]   = useState({})
  const [isLoading, setIsLoading]     = useState(false)

  const filters = Object.fromEntries(new URLSearchParams(location.search))

  const initialPerPage          = filters?.per_page || PER_PAGE_OPTIONS[0].value
  const [perPage, setPerPage]   = useState(initialPerPage)

  const initialSortBy           = filters?.sort_by  || SORT_OPTIONS[0].sort_by
  const initialSortDir          = filters?.sort_dir || SORT_OPTIONS[0].sort_dir
  const [sortBy, setSortBy]     = useState(initialSortBy)
  const [sortDir, setSortDir]   = useState(initialSortDir)

  // FIX: use the real pathname
  const pathName = location.pathname

  useEffect(() => {
    handleFetchDeals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const handleFetchDeals = () => {
    setIsLoading(true)
    const isEmptyParams = Object.keys(filters).length === 0
    const params = isEmptyParams ? DEFAULT_FILTERS : filters

    OutboundDealsApi.get({ ...params })
      .then((response) => {
        setDeals(response.data.data)
        setPagination(response.data.meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data))
      .finally(() => setIsLoading(false))
  }

  const handleSubmitFilter = (values, formik) => {
    // remove empty keys and reset page
    let params = ParamsUtil.removeEmptyAttributes(values)
    params = ParamsUtil.removeAttributeFromObject('page', params)

    params = {
      ...params,
      per_page : perPage,
      sort_by  : sortBy,
      sort_dir : sortDir
    }

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })

    formik.setSubmitting(false)
  }

  const setPerPageToDefault = () => setPerPage(PER_PAGE_OPTIONS[0].value)
  const setSortToDefault    = () => { setSortBy(SORT_OPTIONS[0].sort_by); setSortDir(SORT_OPTIONS[0].sort_dir) }

  const handleResetFilter = () => {
    setPerPageToDefault()
    setSortToDefault()
    history.push({ pathname: pathName })
  }

  const handlePageChange = (page) => {
    const params = { ...filters, page }
    history.push({ pathname: pathName, search: new URLSearchParams(params).toString() })
  }

  const handlePerPageOnChange = (event) => {
    const selectedIndex = event.nativeEvent.target.selectedIndex
    const selectedValue = event.nativeEvent.target[selectedIndex].value
    setPerPage(selectedValue)

    const params = { ...filters, per_page: selectedValue }
    history.push({ pathname: pathName, search: new URLSearchParams(params).toString() })
  }

  const handleSortOnClick = (option) => {
    setSortBy(option.sort_by)
    setSortDir(option.sort_dir)

    const params = { ...filters, sort_by: option.sort_by, sort_dir: option.sort_dir }
    history.push({ pathname: pathName, search: new URLSearchParams(params).toString() })
  }

  const handleClickRemoveFilter = (key) => {
    let params = { ...filters, [key]: '' }
    if (key === 'per_page') setPerPageToDefault()
    if (key === 'sort_dir' || key === 'sort_by') {
      setSortToDefault()
      params['sort_by']  = ''
      params['sort_dir'] = ''
    }
    params = ParamsUtil.removeEmptyAttributes(params)
    history.push({ pathname: pathName, search: new URLSearchParams(params).toString() })
  }

  const handleClickRow = (dealId) => history.push({ pathname: `/app/outbound/deals/${dealId}` })

  const hiddenColumns = []

  return (
    <>
      <TitlePage mainTitle={'Outbound Shipment'} subTitle={'List'} />
      <Row>
        <Col
          className='col-xl-3'
          style={{ height: '80vh', overflow: 'scroll', position: 'sticky', top: 0 }}
        >
          <div>
            <OutboundDealsPagingFilterField
              perPage={perPage}
              sortBy={sortBy}
              sortDir={sortDir}
              handlePerPageOnChange={handlePerPageOnChange}
              handleSortOnClick={handleSortOnClick}
            />
          </div>

          <div>
            <OutboundDealsFilterForm
              onClickSubmitForm={handleSubmitFilter}
              filterValues={filters}
              onClickReset={handleResetFilter}
            />
          </div>
        </Col>

        <Col className='col-xl-9'>
          <Card>
            <div className='m-3'>
              <h5 className='text-muted text-uppercase'>{'Applied Filters'}</h5>
              {
                Object.keys(filters).length > 0
                  ? Object.keys(filters).map((key) => (
                      <Badge color='primary' key={`outbound-deals-filters-${key}`}>
                        <div className='d-flex justify-content-between'>
                          <span className='align-self-center'>
                            {key}: {filters[key]}
                          </span>
                          <Button close onClick={() => handleClickRemoveFilter(key)} />
                        </div>
                      </Badge>
                    ))
                  : <Badge color='warning'>{'no filter applied'}</Badge>
              }
            </div>
          </Card>

          <Card>
            <OutboundDealsUrlParamsTable
              tableProps={{ size: 'sm', responsive: true, hover: true }}
              deals={deals}
              pagination={pagination}
              isLoading={isLoading}
              onPageChange={handlePageChange}
              onClickRow={handleClickRow}
              hiddenColumns={hiddenColumns}
            />
          </Card>
        </Col>
      </Row>
    </>
  )
}

OutboundShipmentListPage.propTypes = propTypes
export default OutboundShipmentListPage
